import { ReactComponent as ActiveIcon } from '@assets/components/panel/sensors/progressionSteps/active-step.svg';
import { ReactComponent as ActiveSubIcon } from '@assets/components/panel/sensors/progressionSteps/active-sub-step.svg';
import { ReactComponent as CompletedIcon } from '@assets/components/panel/sensors/progressionSteps/completed-step.svg';
import { ReactComponent as CompletedSubIcon } from '@assets/components/panel/sensors/progressionSteps/completed-sub-step.svg';
import { ReactComponent as UpcomingIcon } from '@assets/components/panel/sensors/progressionSteps/upcoming-step.svg';
import { ReactComponent as UpcomingSubIcon } from '@assets/components/panel/sensors/progressionSteps/upcoming-sub-step.svg';
import { IcChevronLight } from '@assets/icons/24x24';
import { Box, For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';

import { EditableType } from '@/types/sensor/sensorInstallation.types';

import { useSensorInstallationContext } from '../SensorInstallationContext';
import {
  Step as StepType,
  StepStateEnum,
  SubStep as SubStepType,
  useSensorEditingContext,
} from './SensorEditingContext';

function SubStep<T extends EditableType>({
  subStep,
  subStepIndex,
  stepIndex,
  isEdition,
}: Readonly<{ subStep: SubStepType<T>; subStepIndex: number; stepIndex: number; isEdition: boolean }>) {
  const { getSubStepState, goTo, progress, steps } = useSensorEditingContext<T>();
  const state = getSubStepState(stepIndex, subStepIndex);
  const isActive = state === StepStateEnum.ACTIVE;
  const isCurrentSubStep = progress.subStepIndex === subStepIndex;
  const step = steps[stepIndex];
  const isClickDisabled = !isEdition || isCurrentSubStep || (step.canLoop && subStepIndex !== 0);

  const handleClick = () => {
    !isClickDisabled && goTo(stepIndex, subStepIndex);
  };
  const getStateIcon = (state: StepStateEnum) => {
    if (isEdition && state !== StepStateEnum.ACTIVE) {
      return CompletedSubIcon;
    }
    switch (state) {
      case StepStateEnum.ACTIVE:
        return ActiveSubIcon;
      case StepStateEnum.COMPLETED:
        return CompletedSubIcon;

      case StepStateEnum.UPCOMING:
        return UpcomingSubIcon;
    }
  };
  return (
    <Box
      height="20px"
      width="100%"
      position="relative"
      paddingLeft={9.5}
      cursor={isClickDisabled ? 'default' : 'pointer'}
      onClick={handleClick}
    >
      <Icon as={getStateIcon(state)} position="absolute" boxSize="12px" left="6px" />
      <Text color={isActive ? 'skyBlue' : 'neutral.300'} whiteSpace="pre-line">
        <FormattedMessage id={`components.sensorInstallation.form.${subStep.label}`} defaultMessage={subStep.label} />
      </Text>
    </Box>
  );
}

function Step<T extends EditableType>({
  step,
  stepIndex,
  isEdition,
}: Readonly<{ step: StepType<T>; stepIndex: number; isEdition: boolean }>) {
  const { getStepState, goTo, progress } = useSensorEditingContext<T>();
  const state = getStepState(stepIndex);
  const isCurrentStep = progress.stepIndex === stepIndex;
  const handleClick = () => isEdition && !isCurrentStep && goTo(stepIndex);

  const getStateIcon = (state: StepStateEnum) => {
    if (isEdition && state !== StepStateEnum.ACTIVE) {
      return <Icon as={CompletedIcon} position="absolute" boxSize="24px" left={0} />;
    }
    switch (state) {
      case StepStateEnum.ACTIVE:
        return <Icon as={ActiveIcon} position="absolute" boxSize="36px" left="-6px" />;
      case StepStateEnum.COMPLETED:
      case StepStateEnum.UPCOMING:
        return (
          <Icon
            as={state === StepStateEnum.COMPLETED ? CompletedIcon : UpcomingIcon}
            position="absolute"
            boxSize="24px"
            left={0}
          />
        );
    }
  };
  const isActive = state === StepStateEnum.ACTIVE;
  const color = isActive ? 'neutral.white' : 'neutral.300';

  return (
    <VStack width="100%" gap={5} alignItems="start">
      <HStack
        width="100%"
        height="24px"
        gap={1}
        paddingLeft={7}
        position="relative"
        justifyContent="start"
        cursor={isEdition && !isCurrentStep ? 'pointer' : 'default'}
        onClick={handleClick}
      >
        {getStateIcon(state)}
        <Text fontWeight="medium" color={color} whiteSpace="pre-line">
          <FormattedMessage id={`components.sensorInstallation.form.${step.label}`} defaultMessage={step.label} />
        </Text>
        {step.children && (
          <Icon as={IcChevronLight} boxSize="24px" color={color} transform={isActive ? undefined : 'rotate(180deg)'} />
        )}
      </HStack>
      {step.children && isActive && (
        <For each={step.children}>
          {(subStep, subStepIndex) => (
            <SubStep<T>
              key={subStepIndex}
              subStep={subStep}
              subStepIndex={subStepIndex}
              stepIndex={stepIndex}
              isEdition={isEdition}
            />
          )}
        </For>
      )}
    </VStack>
  );
}

export default function SensorFormSteps<T extends EditableType>() {
  const { steps } = useSensorEditingContext<T>();
  const { isNewSensor } = useSensorInstallationContext();
  return (
    <VStack gap={5} alignItems="start" position="relative" marginLeft={2.5}>
      <Box
        position="absolute"
        height="stretch"
        marginBottom={1.5}
        left={1}
        top={1.5}
        width="8px"
        backgroundColor="neutral.900"
      />
      <For each={steps}>
        {(step, stepIndex) => <Step<T> key={stepIndex} stepIndex={stepIndex} step={step} isEdition={!isNewSensor} />}
      </For>
    </VStack>
  );
}
